import { useCallback, useEffect, useState } from 'react';
import {
  clearDesktopBackendBaseUrl,
  isDesktopApp,
  setDesktopBackendBaseUrl,
  setDesktopStorageBackend,
  usesRemoteDesktopBackend,
} from '@/services/config/env';
import { validateDesktopConfig } from '@/services/api/desktopConfig';
import { waitForBackendReady } from '@/services/api/backendHealth';
import { readEmbeddedDesktopBootstrap } from '@/lib/desktopBootstrap';

const BOOTSTRAP_TIMEOUT_MS = 120_000;

const screenStyle = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  padding: 24,
  background: 'linear-gradient(160deg, #ede9fe 0%, #e0f2fe 45%, #fce7f3 100%)',
  color: '#1e293b',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

export function DesktopBackendGate({ children }) {
  const [phase, setPhase] = useState(isDesktopApp() ? 'loading' : 'ready');
  const [error, setError] = useState('');

  const applySession = useCallback((result) => {
    const validation = validateDesktopConfig(result.config);
    if (!validation.ok && !result.config?.remote) {
      throw new Error(
        `Missing: ${validation.missing.join(', ')}. Add to backend/.env and run install-desktop-env.sh`,
      );
    }
    setDesktopBackendBaseUrl(result.baseUrl);
    setDesktopStorageBackend(result.config?.storage_backend || null);
  }, []);

  const bootstrap = useCallback(async () => {
    if (!isDesktopApp()) {
      setPhase('ready');
      return;
    }

    setPhase('loading');
    setError('');
    clearDesktopBackendBaseUrl();

    const timeout = new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error('Backend startup timed out. Check Application Support/twin-tales-desktop/logs/')),
        BOOTSTRAP_TIMEOUT_MS,
      );
    });

    try {
      if (usesRemoteDesktopBackend()) {
        const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        setDesktopBackendBaseUrl(baseUrl);
        setDesktopStorageBackend(null);
        await Promise.race([
          waitForBackendReady({ timeoutMs: BOOTSTRAP_TIMEOUT_MS }),
          timeout,
        ]);
        setPhase('ready');
        return;
      }

      const embedded = readEmbeddedDesktopBootstrap();
      if (embedded?.baseUrl) {
        applySession(embedded);
        setPhase('ready');
        return;
      }

      if (window.twinTales?.waitForBackendReady) {
        const result = await Promise.race([
          window.twinTales.waitForBackendReady(),
          timeout,
        ]);
        applySession(result);
        setPhase('ready');
        return;
      }

      throw new Error('Rebuild Twin Tales.app from latest source — desktop session not embedded.');
    } catch (err) {
      clearDesktopBackendBaseUrl();
      setError(err?.message || 'Could not connect to backend');
      setPhase('error');
    }
  }, [applySession]);

  useEffect(() => {
    bootstrap();
    return () => clearDesktopBackendBaseUrl();
  }, [bootstrap]);

  if (!isDesktopApp() || phase === 'ready') return children;

  if (phase === 'loading') {
    return (
      <div style={screenStyle}>
        <p style={{ margin: 0, fontSize: 14 }}>
          {usesRemoteDesktopBackend()
            ? 'Connecting to Twin Tales cloud…'
            : 'Loading config from backend/.env…'}
        </p>
      </div>
    );
  }

  return (
    <div style={screenStyle}>
      <h1 style={{ margin: 0, fontSize: 18 }}>Backend not ready</h1>
      <p style={{ margin: 0, fontSize: 14, maxWidth: 400, textAlign: 'center' }}>{error}</p>
      <button type="button" onClick={bootstrap} style={{ marginTop: 8, padding: '10px 20px' }}>
        Retry
      </button>
    </div>
  );
}
